/* eslint-disable @typescript-eslint/no-unsafe-argument */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
// Проблема консистентности типов - в react-admin ID обязательный, и с бэка они приходят, но генерируемые типы там описаны неверно

import {
  type CreateParams,
  type CreateResult,
  type DataProvider,
  type DeleteParams,
  type DeleteResult,
  type GetListParams,
  type GetListResult,
  type GetManyParams,
  type GetManyResult,
  type GetOneParams,
  type GetOneResult,
  type QueryFunctionContext,
  type UpdateParams,
  type UpdateResult,
} from "react-admin";

import { type AvailableTypes, type components, type DefaultType } from "@/api";

type ExtractKeys<
  T extends Record<string, unknown>,
  Entity extends string,
  Format extends keyof AvailableTypes,
  Mode extends "read" | "write",
> = {
  [K in keyof T]: K extends `${Entity}${Format extends "jsonld" ? ":jsonld" : ""}-${string}:${string}${Mode}`
    ? K
    : never;
}[keyof T];

export type GetEntityTypes<
  Entity extends string = never,
  Format extends keyof AvailableTypes = DefaultType,
  T extends components["schemas"] = components["schemas"],
> = {
  read: Omit<T[ExtractKeys<T, Entity, Format, "read">], "id"> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    id?: any;
  };
  write: T[ExtractKeys<T, Entity, Format, "write">];
};

export interface DataProviderSchema<
  Entity extends string,
  Format extends keyof AvailableTypes = DefaultType,
> {
  list: (
    params: GetListParams & QueryFunctionContext,
    resource: string,
  ) => Promise<GetListResult<GetEntityTypes<Entity, Format>["read"]>>;
  detail?: (
    params: GetOneParams<GetEntityTypes<Entity, Format>["read"]> &
      QueryFunctionContext,
  ) => Promise<GetOneResult<GetEntityTypes<Entity, Format>["read"]>>;
  edit?: (
    params: UpdateParams<GetEntityTypes<Entity, Format>["write"]>,
  ) => Promise<UpdateResult<GetEntityTypes<Entity, Format>["read"]>>;
  create?: (
    params: CreateParams<GetEntityTypes<Entity, Format>["write"]>,
  ) => Promise<CreateResult<GetEntityTypes<Entity, Format>["read"]>>;
  delete?: (
    params: DeleteParams<GetEntityTypes<Entity, Format>["read"]>,
  ) => Promise<
    DeleteResult<GetEntityTypes<Entity, Format>["read"] | undefined>
  >;
  many?: (
    params: GetManyParams<GetEntityTypes<Entity, Format>["read"]>,
  ) => Promise<GetManyResult<GetEntityTypes<Entity, Format>["read"]>>;
}

export interface Schemas {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [url: string]: DataProviderSchema<any, any>;
}

export const createDataProvider = (schemas: Schemas): DataProvider => ({
  getList: (resource: string, params: GetListParams & QueryFunctionContext) =>
    schemas[resource].list(params, resource),
  getOne: (resource: string, params: GetOneParams & QueryFunctionContext) => {
    const method = schemas[resource].detail;
    if (!method) throw new Error("Function not implemented.");
    return method(params);
  },
  update: (resource: string, params: UpdateParams) => {
    const method = schemas[resource].edit;
    if (!method) throw new Error("Function not implemented.");
    return method(params);
  },
  create: async (resource: string, params: CreateParams) => {
    const method = schemas[resource].create;
    if (!method) throw new Error("Function not implemented.");
    return method(params);
  },
  delete: async (resource: string, params: DeleteParams) => {
    const method = schemas[resource].delete;
    if (!method) throw new Error("Function not implemented.");
    return method(params);
  },
  // Все методы ниже backend не предоставляет, но их можно эмулировать, чтобы reference поля работали из коробки
  getMany: (resource, params) => {
    const method = schemas[resource].many;
    if (!method) throw new Error("Function not implemented.");
    return method(params);
  },
  deleteMany: () => {
    throw new Error("Function not implemented.");
  },
  getManyReference: () => {
    throw new Error("Function not implemented.");
  },
  updateMany: () => {
    throw new Error("Function not implemented.");
  },
});
